import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ChevronDown, LogOut, Repeat, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Navbar = () => {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doSwitch = async () => {
    const next = user.role === "candidate" ? "recruiter" : "candidate";
    await switchRole(next);
    toast.success(`Switched to ${next} view`);
    navigate(next === "candidate" ? "/candidate" : "/recruiter");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0F19]/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={user ? (user.role === "recruiter" ? "/recruiter" : "/candidate") : "/"} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#3B82F6] to-[#06B6D4] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#0B0F19]" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Axiony</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {!user && (
            <>
              <a
                href="/#pricing"
                onClick={(e) => {
                  if (loc.pathname === "/") {
                    e.preventDefault();
                    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                data-testid="nav-pricing"
                className="px-3 py-1.5 text-sm rounded-md transition-colors text-slate-400 hover:text-white"
              >
                Pricing
              </a>
              <a
                href="/#team"
                onClick={(e) => {
                  if (loc.pathname === "/") {
                    e.preventDefault();
                    document.getElementById("team")?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                data-testid="nav-team"
                className="px-3 py-1.5 text-sm rounded-md transition-colors text-slate-400 hover:text-white"
              >
                Team
              </a>
              <NavLink to="/blog" active={loc.pathname.startsWith("/blog")}>Blog</NavLink>
            </>
          )}
          {user?.role === "candidate" && (
            <>
              <NavLink to="/candidate" active={loc.pathname === "/candidate"}>Dashboard</NavLink>
              <NavLink to="/jobs" active={loc.pathname.startsWith("/jobs")}>Jobs</NavLink>
              <NavLink to="/candidate/resume" active={loc.pathname === "/candidate/resume"}>Resume</NavLink>
              <NavLink to="/candidate/applications" active={loc.pathname === "/candidate/applications"}>My Applications</NavLink>
            </>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" active={loc.pathname.startsWith("/admin")}>Admin</NavLink>
          )}
          {user?.role === "recruiter" && (
            <>
              <NavLink to="/recruiter" active={loc.pathname === "/recruiter"}>Dashboard</NavLink>
              <NavLink to="/recruiter/post" active={loc.pathname === "/recruiter/post"}>Post Job</NavLink>
              <NavLink to="/recruiter/team" active={loc.pathname === "/recruiter/team"}>Team</NavLink>
              <NavLink to="/admin/quotes" active={loc.pathname === "/admin/quotes"}>Quotes</NavLink>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="text-slate-200 hover:text-white">
                Sign in
              </Button>
              <Button onClick={() => navigate("/signup")} data-testid="nav-signup-btn" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-5">
                Get started
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/5 transition-colors" data-testid="nav-user-menu">
                  <Avatar className="w-8 h-8 border border-white/10">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[#1F2937] text-slate-200 text-xs font-medium">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm text-slate-200">{user.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#111827] border-white/10 text-slate-200">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user.name}</div>
                  <div className="text-xs text-slate-400">{user.email}</div>
                  <div className="mono-label mt-1">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={doSwitch} data-testid="nav-switch-role" className="focus:bg-white/5">
                  <Repeat className="w-4 h-4 mr-2" /> Switch to {user.role === "candidate" ? "recruiter" : "candidate"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="nav-logout-btn"
                  className="focus:bg-white/5 text-red-300"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};

const NavLink = ({ to, active, children }) => (
  <Link
    to={to}
    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${active ? "text-white bg-white/5" : "text-slate-400 hover:text-white"}`}
  >
    {children}
  </Link>
);
